import React, { use, useCallback } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { DocumentType, Document, Address } from "../../__generated__/graphql";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { UPDATE_USER } from "../../graphql/identity";
import AddressField from "../../components/address/AddressField";

type FormData = {
    address?: Address;
};

type HomeAddressEditorScreenProps = StackScreenProps<RootStackParamList, "HomeAddressEdit">;

const HomeAddressEditorScreen = ({ navigation, route }: HomeAddressEditorScreenProps) => {
    const { profileId } = route.params || {};
    const { user, refreshUser } = use(AuthManagerContext);
    const [updateUser, { error: saveError, loading: saving, reset }] = useMutation(
        UPDATE_USER,
        profileId
            ? {
                  context: { liftProfile: profileId },
              }
            : undefined,
    );

    useApolloErrorSnackbar(saveError, reset);

    const i18n = useI18n();

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            address: user?.workerInfo?.address || undefined,
        },
    });

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateUser({
                    variables: {
                        userData: {
                            address: data.address,
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by updateError in useMutation
            }
        },
        [updateUser, navigation, refreshUser],
    );

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <ScrollView>
                <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                    <VStack gap={0.25}>
                        <Controller
                            control={control}
                            name="address"
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <AddressField
                                    label={i18n.t("screens.profile_editor.home_address.title")}
                                    value={value}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={!!error?.message}
                                />
                            )}
                        />
                    </VStack>
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

export default HomeAddressEditorScreen;
